import type { PlotData } from 'plotly.js';
import type { SampleRow } from '@/types/sample';
import {
  GENERATED_SAMPLE_COLOR,
  SELECT_COLOR,
  TARGET_COLOR,
  UNSELECTED_OPACITY,
} from '@/constants/colors';

/** Partial Plotly trace type for convenient builder returns. */
export type PartialTrace = Partial<PlotData>;

/** Extract numeric column values from sample rows. */
function extractColumn(rows: SampleRow[], col: string): (number | null)[] {
  return rows.map((r) => {
    const v = r[col];
    if (v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  });
}

/** Build training data trace (faded, hollow circles, non-interactive). */
export function buildTrainingTrace(
  rows: SampleRow[],
  xCol: string,
  yCol: string,
): PartialTrace {
  return {
    x: extractColumn(rows, xCol) as number[],
    y: extractColumn(rows, yCol) as number[],
    mode: 'markers',
    name: 'Training Data',
    marker: {
      size: 6,
      symbol: 'circle-open',
      color: `rgba(130, 150, 180, 0.20)`,
      line: { width: 2, color: '#2b2b2b' },
    },
    hoverinfo: 'skip',
    selected: { marker: { opacity: 0.7, color: 'rgba(130,150,180,0.25)' } },
    unselected: { marker: { opacity: 0.25 } },
  } as PartialTrace;
}

/** Build generated (new) samples trace (orange filled circles). */
export function buildGeneratedTrace(
  rows: SampleRow[],
  xCol: string,
  yCol: string,
): PartialTrace {
  return {
    x: extractColumn(rows, xCol) as number[],
    y: extractColumn(rows, yCol) as number[],
    mode: 'markers',
    name: 'Calculated Designs',
    marker: {
      size: 8,
      symbol: 'circle',
      color: GENERATED_SAMPLE_COLOR,
      opacity: 0.95,
      line: { width: 0.6, color: '#333' },
    },
    customdata: rows.map((r) => r.id as string),
    selected: { marker: { color: SELECT_COLOR, size: 10, opacity: 1.0 } },
    unselected: { marker: { opacity: UNSELECTED_OPACITY } },
  } as PartialTrace;
}

/** Build target point trace (purple cross marker). */
export function buildTargetTrace(
  tx: number | null,
  ty: number | null,
  xCol: string,
  yCol: string,
): PartialTrace {
  const hasTarget = tx !== null && ty !== null;
  return {
    x: hasTarget ? [tx] : [],
    y: hasTarget ? [ty] : [],
    mode: 'markers',
    name: 'Target',
    marker: {
      size: 12,
      symbol: 'x',
      color: TARGET_COLOR,
      line: { width: 2, color: '#222' },
    },
    hovertemplate: `${xCol}: %{x}<br>${yCol}: %{y}<extra>Target</extra>`,
    showlegend: true,
  } as PartialTrace;
}

/** Build best-sample trace (purple star marker). */
export function buildBestSampleTrace(
  bx: number | null,
  by: number | null,
  bestId: string | null,
  xCol: string,
  yCol: string,
): PartialTrace {
  const hasBest = bx !== null && by !== null;
  return {
    x: hasBest ? [bx] : [],
    y: hasBest ? [by] : [],
    mode: 'markers',
    name: 'Best Design',
    marker: {
      size: 14,
      symbol: 'star',
      color: TARGET_COLOR,
      line: { width: 1.2, color: '#222' },
    },
    customdata: hasBest && bestId ? [bestId] : [],
    hovertemplate: `id=%{customdata}<br>${xCol}: %{x}<br>${yCol}: %{y}<extra>Best</extra>`,
    showlegend: true,
  } as PartialTrace;
}

/**
 * Compute the selected point indices for the generated-samples trace.
 * Returns an array with the index of the selected sample, or empty if none selected.
 */
export function getSelectedPointIndices(
  rows: SampleRow[],
  selectedId: string | null,
): number[] {
  if (!selectedId) return [];
  const idx = rows.findIndex((r) => String(r.id) === selectedId);
  return idx >= 0 ? [idx] : [];
}

/** Default Plotly layout for scatter charts. */
export function buildChartLayout(xLabel: string, yLabel: string) {
  return {
    margin: { l: 60, r: 20, t: 20, b: 60 },
    dragmode: 'zoom' as const,
    clickmode: 'event' as const,
    autosize: true,
    xaxis: {
      title: {
        text: `<b>${xLabel}</b>`,
        standoff: 8,
        font: { size: 13, color: '#222', family: 'Segoe UI Semibold, Segoe UI, Arial, sans-serif' },
      },
      tickfont: { size: 11, color: '#333' },
      ticks: 'outside' as const,
      automargin: true,
      autorange: true,
      fixedrange: false,
      zeroline: false,
      gridcolor: '#e9eef6',
    },
    yaxis: {
      title: {
        text: `<b>${yLabel}</b>`,
        standoff: 5,
        font: { size: 13, color: '#222', family: 'Segoe UI Semibold, Segoe UI, Arial, sans-serif' },
      },
      tickfont: { size: 11, color: '#333' },
      ticks: 'outside' as const,
      automargin: true,
      autorange: true,
      fixedrange: false,
      zeroline: false,
      gridcolor: '#e9eef6',
    },
    plot_bgcolor: '#f7f9fc',
    paper_bgcolor: 'rgba(0,0,0,0)',
    showlegend: true,
    legend: {
      orientation: 'h' as const,
      yanchor: 'top' as const,
      y: 1.0,
      xanchor: 'left' as const,
      x: 0.0,
      bgcolor: 'rgba(255,255,255,0.90)',
      bordercolor: '#cfcfcf',
      borderwidth: 1,
      font: { size: 13, color: '#222', family: 'Segoe UI Semibold, Segoe UI, Arial, sans-serif' },
    },
  };
}
